import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BasicTable,
  Button,
  ContentBox,
  ContentPage,
  DropdownAction,
  NoResults,
  Notice,
  PageActions,
  Skeleton,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { escapeHtml, formatDateTime, usePageHeader } from "@components/ow";
import DeleteGroupModal from "./DeleteGroupModal";
import GroupNameModal from "./GroupNameModal";

// Grupos confidenciais (modelo Lista do DS OneWork). Só ADM (guard da rota). O back
// manda todos os grupos de uma vez, e cada empresa tem poucos.

const LIST_PATH = "/c/confidential-groups";

const BREADCRUMB = [{ page: "Grupos confidenciais", route: LIST_PATH }];

const NoticeSpace = styled.div`
  margin-bottom: 20px;
`;

export default function ConfidentialGroups() {
  const groupsService = useService("confidentialGroups");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  // { mode: "create" | "rename" | "delete", group }
  const [modal, setModal] = useState(null);

  usePageHeader({ title: "Grupos confidenciais", breadcrumb: BREADCRUMB });

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchList() {
    setLoading(true);
    try {
      const result = await groupsService.listGroups();
      setList(result?.data || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao carregar os grupos confidenciais.");
    } finally {
      setLoading(false);
    }
  }

  const openGroup = (group) => navigate(`${LIST_PATH}/${group.id}`);
  const openCreate = () => setModal({ mode: "create" });
  const closeModal = () => setModal(null);

  // Lança em erro: o GroupNameModal fica aberto
  async function saveName(name) {
    try {
      if (modal.mode === "rename") {
        await groupsService.renameGroup(modal.group.id, name);
        toast.success("Grupo renomeado.");
        setModal(null);
        fetchList();
        return;
      }
      const result = await groupsService.createGroup(name);
      toast.success("Grupo criado. Agora inclua as pessoas.");
      openGroup(result.data);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao salvar o grupo.");
      throw err;
    }
  }

  function afterDelete() {
    setModal(null);
    fetchList();
  }

  const columns = [
    {
      id: "name",
      header: "Grupo",
      size: 460,
      cell: ({ row }) => <Text $text={escapeHtml(row.original.name)} $variant="paragraph_small_bold" $color="N900" />,
    },
    { id: "members", header: "Pessoas", size: 140, cell: ({ row }) => row.original.members_count },
    { id: "viewers", header: "Quem pode ver", size: 160, cell: ({ row }) => row.original.viewers_count ?? 0 },
    {
      id: "created_at",
      header: "Criado em",
      size: 200,
      cell: ({ row }) => (row.original.created_at ? formatDateTime(row.original.created_at) : "—"),
    },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => (
        <DropdownAction
          options={[
            { label: "Ver pessoas", action: () => openGroup(row.original) },
            { label: "Renomear", action: () => setModal({ mode: "rename", group: row.original }) },
            { label: "Excluir", action: () => setModal({ mode: "delete", group: row.original }), isImportant: true },
          ]}
        />
      ),
    },
  ];

  return (
    <ContentPage>
      <PageActions>
        <Button $text="Novo grupo" $variant="primary" $action={openCreate} />
      </PageActions>

      <ContentBox>
        <NoticeSpace>
          <Notice
            $variant="gray"
            $text="Contratos, cronogramas, valores, notas fiscais, o ponto e as horas das pessoas de um grupo só aparecem para os administradores e para quem tiver permissão no grupo. O cadastro e os dados bancários continuam visíveis para quem já tem acesso a eles."
          />
        </NoticeSpace>

        {loading ? (
          <Skeleton $height={240} />
        ) : (
          <BasicTable
            data={list}
            columns={columns}
            getRowId={(row) => String(row.id)}
            onRowClick={openGroup}
            noResults={
              <NoResults notResultText="Nenhum grupo confidencial por aqui" includeText="Novo grupo" includeAction={openCreate} />
            }
          />
        )}
      </ContentBox>

      {modal?.mode === "create" || modal?.mode === "rename" ? (
        <GroupNameModal group={modal.group} onClose={closeModal} onSave={saveName} />
      ) : null}
      {modal?.mode === "delete" ? (
        <DeleteGroupModal
          group={modal.group}
          onClose={closeModal}
          onDeleted={afterDelete}
          onViewMembers={() => openGroup(modal.group)}
        />
      ) : null}
    </ContentPage>
  );
}
